import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowLeftLine} from '@dropbox/dig-icons/assets';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {Layout} from 'components/DSYS/Layout';
import {DSYS_PAGE_GUTTER_PX} from 'components/DSYS/layoutConstants';
import {PresenceFacepile} from 'components/DSYS/PresenceFacepile';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {useAtomValue} from 'jotai';
import {type ReactNode, useEffect, useState} from 'react';
import {WorkstreamActivitySection} from 'views/workstreams/detail/WorkstreamActivitySection';
import {WorkstreamDetailsHero} from 'views/workstreams/detail/WorkstreamDetailsHero';
import {
  type WorkstreamCustomPrivacyConfig,
  WorkstreamDetailsSidebar,
} from 'views/workstreams/detail/WorkstreamDetailsSidebar';
import {HighlightNorthStarRowContext} from 'views/workstreams/detail/WorkstreamMetricsQuarterTable';
import {WorkstreamMetricsSection} from 'views/workstreams/detail/WorkstreamMetricsSection';
import {WorkstreamOutcomesSection} from 'views/workstreams/detail/WorkstreamOutcomesSection';
import {SectionCardTitleStyleContext} from 'views/workstreams/detail/WorkstreamSectionCard';
import {useWorkstreamStaffingState} from 'views/workstreams/detail/WorkstreamStaffing';
import {WorkstreamStrategySection} from 'views/workstreams/detail/WorkstreamStrategySection';
import {WorkstreamTimeframeChip} from 'views/workstreams/detail/WorkstreamTimeframeChip';
import {useWorkstream, useWorkstreamDisplayStatus} from 'views/workstreams/hooks';
import {isRestrictedWorkstreamAccessResponse} from 'views/workstreams/utils';
import {WorkstreamAccessRequest} from 'views/workstreams/WorkstreamAccessRequest';

import {ArchivedTitleChip} from './ArchivedTitleChip';
import {CustomPrivacyModal} from './CustomPrivacyModal';
import {LatestUpdateSummary} from './LatestUpdateSummary';
import {
  flashElement,
  MEMBERS_LIST_ID,
  MEMBERS_SECTION_ID,
  MembersDisclosure,
} from './MembersDisclosure';
import {PinToggleButton} from './PinToggleButton';
import {SectionLabel} from './SectionLabel';
import {offersStaffing, StaffingButton} from './StaffingButton';
import {SubworkstreamsSection} from './SubworkstreamsSection';
import {UpdateHistory} from './UpdateHistory';
import {useHoverReveal} from './useHoverReveal';
import {WorkstreamDetailSkeleton} from './WorkstreamDetailSkeleton';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';
import {WorkstreamLoadFailure} from './WorkstreamsControls';

// The page's reading column (the title's max width). Metrics and
// Sub-workstreams run the full width; everything else stays in this.
const Column = ({children}: {children: ReactNode}) => (
  <Layout.MaxWidth width="reading">{children}</Layout.MaxWidth>
);

// Esc closes the page, like the back arrow. On window, so it runs after document
// handlers: an expanded sub-workstream card collapses first (and marks the event
// handled). Typing, open dialogs, and menus keep their own Esc.
// The back arrow's 24px button + 8px gap, hung left of the title.
const BACK_ARROW_SLOT_PX = 32;
// Where the arrow's left edge sits: a little inside the cards' 32px edge (the
// page's one exception), so the hung arrow doesn't push the content too far in.
const BACK_ARROW_LEFT_PX = 16;
// How much further in than the page gutter that puts the content.
const BACK_ARROW_INDENT_PX = BACK_ARROW_LEFT_PX + BACK_ARROW_SLOT_PX - DSYS_PAGE_GUTTER_PX;

const useEscapeToClose = (onClose?: () => void) => {
  useEffect(() => {
    if (!onClose) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable="true"]')) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return;
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
};

/**
 * /workstreams (default list view): the workstream detail page in the card
 * list's visual language. Header = title / objective, then one unlabeled
 * properties line (status button, readiness, the DRI — hover lists every role,
 * click opens Members, joined by "| N FTE" behind the headcount flag for
 * workstreams in the half — privacy, resources and "Part of N" chips, and Archive).
 * Body: updates (scrolling history list beside the selected one), metrics &
 * milestones, sub-workstreams as cards in one box (open, closed, then collapsed Archived),
 * and collapsed Members / Strategy / Activity. One reading column, with only the
 * Metrics table and Sub-workstreams running the full width.
 */
export const WorkstreamDetailView = ({
  workstreamId,
  onLeaveUpdate,
  onClose,
  pinned = false,
  onTogglePin,
  subworkstreamsAsTable = false,
}: {
  workstreamId: number;
  onLeaveUpdate: (id: number) => void;
  // `?table=1`: sub-workstreams as table rows instead of cards.
  subworkstreamsAsTable?: boolean;
  // Back to where the page was opened from (the back arrow and Esc).
  onClose?: () => void;
  // Its recent's pin, the same one the rail and Recently viewed show.
  pinned?: boolean;
  onTogglePin?: () => void;
}) => {
  const {workstream, isLoading, error, refetch} = useWorkstream(workstreamId);
  const {isWorkstreamHeadcountInputEnabled, isWorkstreamTimeframesEnabled} =
    useAtomValue(growthbookCacheAtom);
  // Fetched only behind the flag; the Staffing popover says when it can't be read.
  const staffing = useWorkstreamStaffingState(workstreamId, isWorkstreamHeadcountInputEnabled);
  const {archiveUpdate, showArchivedChip} = useWorkstreamDisplayStatus(workstream ?? {});
  const [strategyExpanded, setStrategyExpanded] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);
  // The properties row portals its people (DRI, sponsor, …) into this list.
  const [membersSlot, setMembersSlot] = useState<HTMLElement | null>(null);
  const [roadmapContentSlot, setRoadmapContentSlot] = useState<HTMLElement | null>(null);
  const [shouldShowRoadmapEmbed, setShouldShowRoadmapEmbed] = useState(false);
  const [membersExpanded, setMembersExpanded] = useState(false);
  // The privacy menu's "Custom" option opens the audience picker.
  const [customPrivacy, setCustomPrivacy] = useState<WorkstreamCustomPrivacyConfig | null>(null);
  // An unpinned pin shows only while the header is hovered (or focused).
  const pinReveal = useHoverReveal();
  useEscapeToClose(onClose);
  const showSkeleton = isLoading && !workstream;
  const fadeInRef = useSkeletonFadeIn(showSkeleton);
  const toggleSection =
    (section: 'members' | 'strategy' | 'activity', set: (expanded: boolean) => void) =>
    (expanded: boolean) => {
      logWorkstreamsInteraction('section_toggled', {
        section,
        expanded,
        workstream_id: workstreamId,
      });
      set(expanded);
    };
  // The DRI on the properties line opens Members, scrolls to it, and flashes the
  // people list, so the click reads even when the page can't scroll (a tall
  // screen) or it's already open.
  const openMembers = () => {
    logWorkstreamsInteraction('section_toggled', {
      section: 'members',
      expanded: true,
      workstream_id: workstreamId,
      method: 'people_chip',
    });
    setMembersExpanded(true);
    requestAnimationFrame(() => {
      document
        .getElementById(MEMBERS_SECTION_ID)
        ?.scrollIntoView({behavior: 'smooth', block: 'start'});
      flashElement(MEMBERS_LIST_ID);
    });
  };

  if (isRestrictedWorkstreamAccessResponse(workstream as never)) {
    return (
      <WorkstreamAccessRequest
        requested={(workstream as {requested?: boolean | null}).requested ?? false}
        workstreamId={workstreamId}
      />
    );
  }
  if (showSkeleton) {
    return <WorkstreamDetailSkeleton indent={onClose ? BACK_ARROW_INDENT_PX : undefined} />;
  }
  if (!workstream) return <WorkstreamLoadFailure error={error} onRetry={() => void refetch()} />;

  return (
    // Section cards render quiet sentence-case titles here (not uppercase eyebrows).
    <SectionCardTitleStyleContext.Provider value="label">
      <Box ref={fadeInRef} display="flex" style={{minWidth: 0, minHeight: '100%'}}>
        {/* The band Sub-workstreams' drag handles show across (usePointerBand). */}
        <Box data-pointer-band-root paddingY="24" style={{flex: 1, minWidth: 0}}>
          {/* The page gutter, like the card list's and the Metrics view's. With a
              back arrow, the left side is the arrow's spot (BACK_ARROW_LEFT_PX) plus
              its slot (24px button + 8px gap), so the content clears the hung arrow;
              the indent comes off the cap, so the right edge matches the cards'. */}
          <Layout.MaxWidth
            width="reading"
            inset
            indent={onClose ? BACK_ARROW_INDENT_PX : undefined}
          >
            <Stack gap="24">
              {/* Header: title + objective, then the properties row (status, people,
                privacy, resources) — the old right rail, laid out horizontally. */}
              <Box
                {...pinReveal.regionProps}
                display="flex"
                alignItems="flex-start"
                style={{gap: 12}}
              >
                <Box style={{flex: 1, minWidth: 0}}>
                  <Column>
                    <Stack gap="16">
                      <WorkstreamDetailsHero
                        workstream={workstream}
                        titleAccessory={
                          isWorkstreamTimeframesEnabled && workstream.timeframe ? (
                            <Box display="flex" alignItems="center" style={{gap: 8}}>
                              {showArchivedChip && (
                                <ArchivedTitleChip archivedAt={archiveUpdate?.created_at} />
                              )}
                              <WorkstreamTimeframeChip timeframe={workstream.timeframe} />
                            </Box>
                          ) : showArchivedChip ? (
                            <ArchivedTitleChip archivedAt={archiveUpdate?.created_at} />
                          ) : undefined
                        }
                        // Its pin (as a recent), right after the name; always
                        // shown once pinned, like the rail's and the strip's.
                        titleTrailing={
                          onTogglePin && (
                            <Box style={pinned ? undefined : pinReveal.revealStyle}>
                              <PinToggleButton
                                pinned={pinned}
                                onToggle={onTogglePin}
                                target="sidebar"
                              />
                            </Box>
                          )
                        }
                        // Back to the card list, hung left of the title (the full
                        // page sits on top of the cards).
                        titleLeading={
                          onClose && (
                            <Tooltip title="Back (Esc)" placement="bottom">
                              <IconButton
                                variant="borderless"
                                size="small"
                                aria-label="Back"
                                onClick={onClose}
                              >
                                <UIIcon src={ArrowLeftLine} />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                      />
                      <WorkstreamDetailsSidebar
                        workstream={workstream}
                        layout="row"
                        switchboardInResources
                        membersSlot={membersSlot}
                        roadmapContentSlot={roadmapContentSlot}
                        onRoadmapContentVisibilityChange={setShouldShowRoadmapEmbed}
                        onPeopleClick={() => openMembers()}
                        onOpenCustomPrivacy={setCustomPrivacy}
                        afterPeople={
                          // Everyone behind the flag, once the workstream is known to be in
                          // the half; finance readers see the numbers.
                          isWorkstreamHeadcountInputEnabled && offersStaffing(staffing) ? (
                            <StaffingButton staffing={staffing} workstreamId={workstreamId} />
                          ) : undefined
                        }
                      />
                    </Stack>
                  </Column>
                </Box>
                {/* Who's here, at the title row's right end. */}
                <Box flexShrink={0}>
                  <PresenceFacepile size="small" />
                </Box>
              </Box>

              {/* Body: in the column, except the wide Metrics table and Sub-workstreams. */}
              <div ref={setRoadmapContentSlot} hidden={!shouldShowRoadmapEmbed} />
              <Box style={{minWidth: 0}} hidden={shouldShowRoadmapEmbed}>
                <Stack gap="32">
                  <Column>
                    <LatestUpdateSummary workstream={workstream} />
                    <SectionLabel>Updates</SectionLabel>
                    <UpdateHistory workstream={workstream} onLeaveUpdate={onLeaveUpdate} />
                  </Column>

                  {/* The full Metrics / Milestones sections (period picker, edit pencils,
                  Jira milestones), with list-style headers via the context below. */}
                  <HighlightNorthStarRowContext.Provider value>
                    <WorkstreamMetricsSection workstream={workstream} />
                  </HighlightNorthStarRowContext.Provider>
                  <Column>
                    <WorkstreamOutcomesSection workstream={workstream} />
                  </Column>

                  <SubworkstreamsSection
                    workstream={workstream}
                    onLeaveUpdate={onLeaveUpdate}
                    asTable={subworkstreamsAsTable}
                  />

                  {/* Collapsed by default: a tight stack of disclosure rows. Members
                    lists every role (unset ones as a dash; each row edits in place). */}
                  <Column>
                    <Stack gap="12">
                      <MembersDisclosure
                        isExpanded={membersExpanded}
                        onExpandedChange={toggleSection('members', setMembersExpanded)}
                        onSlot={setMembersSlot}
                      />
                      <WorkstreamStrategySection
                        isExpanded={strategyExpanded}
                        onExpandedChange={toggleSection('strategy', setStrategyExpanded)}
                        workstream={workstream}
                        steadyHeader
                      />
                      <WorkstreamActivitySection
                        isExpanded={activityExpanded}
                        onExpandedChange={toggleSection('activity', setActivityExpanded)}
                        workstream={workstream}
                        steadyHeader
                      />
                    </Stack>
                  </Column>
                </Stack>
              </Box>
            </Stack>
          </Layout.MaxWidth>
        </Box>
      </Box>
      {customPrivacy && (
        <CustomPrivacyModal
          workstream={workstream}
          config={customPrivacy}
          onClose={() => setCustomPrivacy(null)}
        />
      )}
    </SectionCardTitleStyleContext.Provider>
  );
};
